import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import { addItemsByParams, replaceItemsByParams } from '@nike/ux-tread-cart';
import { ADD_TO_CARTS_SUCCESS, SERVICE_ATC_CARTS, VAS_INSTRUCTION_TYPE_MAP } from '@nike/cart-constants';
import { DOTCOM_UX_ID, NIKECOM_CHANNEL, NIKE_BRAND } from '@nike/ux-tread-dotcom-constants';
import { fetchClient } from '@nike/fetch-client';
import graphqlFetch from '@nike/graphql-fetch';
import handleCartErrors from './handle-cart-errors';
import { onFetchStart } from './action-creators';
import { getCartItems } from '../selectors';
import { createItemData } from '../utils';

/**
 * updateCart allows merging carts and updating the entire cart
 * Passing in items can add items array to the cart.
 *
 * @param {object} params - named parameters
 * @param {string} [params.appId=DOTCOM_UX_ID] - app id for headers to identify client in logs
 * @param {string} params.token - consumer jwt, user token
 * @param {string} params.visitId - unite visit id
 * @param {string} params.visitorId - unite visitor id
 * @param {string} [params.brand=NIKE_BRAND] - brand is a differentiation for carts, by default it's set to NIKE
 * @param {string} [params.channel=NIKECOM_CHANNEL] - channel is a differentiation for carts, by default it's set to NIKECOM
 * @param {string} params.country - 2 letter ISO country code
 * @param {string} params.cloudStack - aws stack name to include in headers, by default undefined which points to production
 * @param {string} params.host - host name to use when calling cart service
 * @param {string} params.id - optionally pass in to edit/replace item
 * @param {Array} [params.items=[]] - array of items to be patched in the cart
 * @param {Array} [params.promotionCodes=[]] - promocodes to apply to cart
 * @param {string} params.designViewIds - customization product identifiers to add or edit in cart
 * @param {string}  [params.offer] - optional UUID for exclusive access offer
 * @param {Array<{ id: string, type: string}>}  [params.offers] - optional offers for ExAx and NCP. This section is not intended for CN
 * @returns {Promise} thenable return with dispatched action results
 */
export default function updateCart(_ref) {
  var offer = _ref.offer,
    offers = _ref.offers,
    token = _ref.token,
    visitId = _ref.visitId,
    visitorId = _ref.visitorId,
    _ref$brand = _ref.brand,
    brand = _ref$brand === void 0 ? NIKE_BRAND : _ref$brand,
    _ref$channel = _ref.channel,
    channel = _ref$channel === void 0 ? NIKECOM_CHANNEL : _ref$channel,
    country = _ref.country,
    cloudStack = _ref.cloudStack,
    host = _ref.host,
    id = _ref.id,
    _ref$items = _ref.items,
    items = _ref$items === void 0 ? [] : _ref$items,
    _ref$appId = _ref.appId,
    appId = _ref$appId === void 0 ? DOTCOM_UX_ID : _ref$appId,
    _ref$promotionCodes = _ref.promotionCodes,
    promotionCodes = _ref$promotionCodes === void 0 ? [] : _ref$promotionCodes,
    designViewIds = _ref.designViewIds;
  return function (dispatch, getState) {
    var itemDataToMerge = createItemData();
    var params = {
      host: host,
      cloudStack: cloudStack,
      token: token,
      visitId: visitId,
      visitorId: visitorId,
      appId: appId,
      brand: brand,
      channel: channel,
      country: country,
      items: items.map(function (item) {
        return _objectSpread(_objectSpread({}, itemDataToMerge), item);
      }) // NOTE - we are most likely to want to preserve any existing itemData field than not. This will avoid overwriting the pdp url if it's actually being edited somewhere.
    };

    var service = SERVICE_ATC_CARTS;
    if (promotionCodes.length) {
      params.promotionCodes = promotionCodes;
    }
    dispatch(onFetchStart(_objectSpread({
      service: service
    }, params)));
    if (designViewIds !== null && designViewIds !== void 0 && designViewIds.length) {
      var updateCartFn = id ? replaceItemsByParams : addItemsByParams;
      return graphqlFetch({
        appId: appId,
        fetchClient: fetchClient,
        cloudStack: cloudStack,
        versionedQuery: 'getskudesignviewids/v1',
        variables: {
          designViewIds: designViewIds,
          country: country
        }
      }).then(function (o) {
        var _o$data;
        var designViews = ((_o$data = o.data) === null || _o$data === void 0 ? void 0 : _o$data.designViews) || [];
        var vasItems = designViewIds.reduce(function (memo, curr, i) {
          memo.push(_objectSpread({
            designViewId: String(curr)
          }, designViews[i]));
          return memo;
        }, []);
        params.items = vasItems.map(function (vas) {
          var _vas$product, _vas$product$valueAdd, _vas$product$valueAdd2, _vas$designVasIds;
          var vasType = vas === null || vas === void 0 ? void 0 : (_vas$product = vas.product) === null || _vas$product === void 0 ? void 0 : (_vas$product$valueAdd = _vas$product.valueAddedServices) === null || _vas$product$valueAdd === void 0 ? void 0 : (_vas$product$valueAdd2 = _vas$product$valueAdd[0]) === null || _vas$product$valueAdd2 === void 0 ? void 0 : _vas$product$valueAdd2.vasType;
          return _objectSpread(_objectSpread({}, itemDataToMerge), {}, {
            id: id,
            offer: offer,
            // offerid and nby is a thing
            offers: offers,
            skuId: vas.skuId,
            quantity: 1,
            valueAddedServices: [{
              id: vas === null || vas === void 0 ? void 0 : (_vas$designVasIds = vas.designVasIds) === null || _vas$designVasIds === void 0 ? void 0 : _vas$designVasIds[0],
              instruction: {
                id: vas.designViewId,
                type: VAS_INSTRUCTION_TYPE_MAP[vasType]
              }
            }]
          });
        });
        return updateCartFn(params).then(function (data) {
          return dispatch({
            type: ADD_TO_CARTS_SUCCESS,
            meta: {
              service: service
            },
            payload: {
              data: data
            }
          });
        }, function (error) {
          return dispatch(handleCartErrors({
            service: service,
            params: params,
            error: error
          }));
        });
      });
    }
    return addItemsByParams(params).then(function (data) {
      return dispatch({
        type: ADD_TO_CARTS_SUCCESS,
        meta: {
          service: service
        },
        payload: {
          data: data
        }
      });
    }, function (error) {
      var _error$data;
      var data = (error === null || error === void 0 ? void 0 : error.data) || {};
      if (!(error !== null && error !== void 0 && (_error$data = error.data) !== null && _error$data !== void 0 && _error$data.items)) {
        data = _objectSpread(_objectSpread({}, (error === null || error === void 0 ? void 0 : error.data) || {}), {}, {
          items: getCartItems(getState())
        });
      }
      return dispatch(handleCartErrors({
        service: service,
        params: params,
        error: _objectSpread(_objectSpread({}, error), {}, {
          data: data
        })
      }));
    });
  };
}